// src/hooks/useAuth.js
import {createContext, useContext, useState, useEffect, useRef} from 'react';
import {loginUser, logoutUser, refreshToken, getUserInfo} from '../api/axios';

const AuthContext = createContext();
const TOKEN_EXPIRATION_TIME = 60 * 60 * 1000; // 60 Minuten

export const AuthProvider = ({children}) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutTimer = useRef(null);
  const refreshInProgress = useRef(false);

  useEffect(() => {
    const handleRefresh = async () => {
      if (refreshInProgress.current) return;
      refreshInProgress.current = true;
      try {
        const response = await refreshToken();
        if (response.data && response.data.access_token) {
          setUser({
            token: response.data.access_token,
            username: response.data.username || 'Guest',
            permission: response.data.permission ?? 1,
            vote_points: response.data.vote_points ?? 0,
            cash_points: response.data.cash_points ?? 0,
            newsletter: response.data.newsletter ?? 0,
            arc_hybrid: response.data.arc_hybrid ?? 0,
            arc_pvp: response.data.arc_pvp ?? 0,
            arc_saison: response.data.arc_saison ?? 0,
          });
          resetLogoutTimer();
        } else {
          setUser(null);
        }
      } catch (error) {
        setUser(null);
      } finally {
        refreshInProgress.current = false;
        setLoading(false);
      }
    };
    handleRefresh();

    // eslint-disable-next-line
  }, []);

  const resetLogoutTimer = () => {
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    logoutTimer.current = setTimeout(() => {
      logout();
    }, TOKEN_EXPIRATION_TIME);
  };

  const login = async loginData => {
    try {
      const response = await loginUser(loginData);
      if (response.data.access_token) {
        setUser({
          token: response.data.access_token,
          username: response.data.username || 'Guest',
          permission: response.data.permission ?? 1,
          vote_points: response.data.vote_points ?? 0,
          cash_points: response.data.cash_points ?? 0,
          newsletter: response.data.newsletter ?? 0,
          arc_hybrid: response.data.arc_hybrid ?? 0,
          arc_pvp: response.data.arc_pvp ?? 0,
          arc_saison: response.data.arc_saison ?? 0,
        });
        resetLogoutTimer();
      }
      return response.data;
    } catch (error) {
      throw error.response?.data?.error || 'Login failed';
    }
  };

  const logout = async () => {
    try {
      await logoutUser();
      setUser(null);
    } catch (error) {
      setUser(null);
    }
  };

  const refreshUserData = async () => {
    if (!user) return;
    try {
      const response = await getUserInfo();
      if (response.data) {
        setUser(prev => ({
          ...prev,
          permission: response.data.permission ?? prev.permission,
          vote_points: response.data.vote_points ?? prev.vote_points,
          cash_points: response.data.cash_points ?? prev.cash_points,
          arc_hybrid: response.data.arc_hybrid ?? prev.arc_hybrid,
          arc_pvp: response.data.arc_pvp ?? prev.arc_pvp,
          arc_saison: response.data.arc_saison ?? prev.arc_saison,
        }));
      }
    } catch (error) {
      // Fehlerbehandlung entfernt.
    }
  };

  return (
    <AuthContext.Provider value={{user, login, logout, loading, refreshUserData}}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
